import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "../api";
import { useLang } from "../i18n";
import PhotoManager from "../components/PhotoManager";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const BE = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u && u.startsWith("/") ? `${BE}${u}` : u);

const EMPTY = {
  title: "", year: "", engine: "", km: "",
  fuel_en: "", fuel_ro: "", desc_en: "", desc_ro: "", images: [],
};

const inputCls =
  "w-full bg-[#0D0F16] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[rgba(68,153,247,0.5)] transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-mono-num tracking-widest uppercase text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export default function AdminParts() {
  const { t } = useLang();
  const [parts, setParts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/parts").then((r) => setParts(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };

  const openEdit = (p) => {
    setForm({
      title: p.title, year: p.year != null ? String(p.year) : "", engine: p.engine || "",
      km: p.km != null ? String(p.km) : "",
      fuel_en: p.fuel_en || "", fuel_ro: p.fuel_ro || "",
      desc_en: p.desc_en || "", desc_ro: p.desc_ro || "",
      images: p.images || [],
    });
    setEditing(p.id);
  };

  const payloadFrom = () => ({
    title: form.title.trim(),
    year: form.year ? Number(form.year) : null,
    engine: form.engine.trim(),
    km: form.km ? Number(form.km) : null,
    fuel_en: form.fuel_en.trim(),
    fuel_ro: form.fuel_ro.trim(),
    desc_en: form.desc_en.trim(),
    desc_ro: form.desc_ro.trim(),
    images: form.images,
  });

  const save = async (e) => {
    e.preventDefault();
    if (!form.images.length) {
      toast.error(t("admin.photos"));
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/parts", payloadFrom());
      } else {
        await api.put(`/parts/${editing}`, payloadFrom());
      }
      toast.success(t("admin.savedPart"));
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(t("admin.deleteConfirm"))) return;
    try {
      await api.delete(`/parts/${p.id}`);
      toast.success(t("admin.deletedPart"));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-parts-panel">
      <div className="flex items-center justify-between gap-4 mt-10">
        <p className="font-mono-num text-xs tracking-[0.25em] uppercase text-slate-500">{parts.length} {t("admin.tabParts")}</p>
        <button
          data-testid="admin-add-part-btn"
          onClick={openNew}
          className="flex items-center gap-2 bg-[#4499F7] text-[#0A0B10] font-semibold text-sm rounded-full px-5 py-2.5 hover:bg-[#6FB1FA] transition-colors"
        >
          <Plus size={16} />
          {t("admin.addPart")}
        </button>
      </div>

      <div className="mt-6 bg-[#12141D] border border-white/10 rounded-3xl overflow-hidden" data-testid="admin-parts-table">
        {parts.length === 0 ? (
          <p className="p-12 text-center text-slate-500" data-testid="admin-parts-empty">{t("admin.partsEmpty")}</p>
        ) : (
          parts.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-part-row-${p.id}`}
              className="flex flex-col lg:flex-row lg:items-center gap-4 px-6 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {p.images?.[0] && (
                  <img src={imgSrc(p.images[0])} alt="" className="w-20 h-14 object-cover rounded-lg border border-white/10 shrink-0" loading="lazy" />
                )}
                <div className="min-w-0">
                  <p className="font-semibold text-slate-100 truncate">{p.title}</p>
                  <p className="text-xs text-slate-500 font-mono-num">
                    {[p.year, p.engine, p.km != null && `${p.km.toLocaleString("en-US")} km`].filter(Boolean).join(" · ") || "—"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`admin-part-edit-${p.id}`}
                  onClick={() => openEdit(p)}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-[#4499F7] transition-colors border border-white/10 rounded-full px-4 py-2"
                >
                  <Pencil size={13} />
                  {t("admin.editVehicle")}
                </button>
                <button
                  data-testid={`admin-part-delete-${p.id}`}
                  onClick={() => remove(p)}
                  className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-400 transition-colors border border-white/10 rounded-full px-4 py-2"
                >
                  <Trash2 size={13} />
                  {t("admin.deleteVehicle")}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-[#12141D] border-white/10 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="part-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-slate-50">
              {editing === "new" ? t("admin.addPart") : t("admin.editVehicle")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div className="sm:col-span-2">
              <Field label={t("admin.fTitle")}>
                <input data-testid="part-form-title" required value={form.title} onChange={set("title")} className={inputCls} />
              </Field>
            </div>
            <Field label={t("admin.fYear")}>
              <input data-testid="part-form-year" type="number" min="1950" max="2030" value={form.year} onChange={set("year")} className={inputCls} />
            </Field>
            <Field label={t("admin.fEngine")}>
              <input data-testid="part-form-engine" value={form.engine} onChange={set("engine")} className={inputCls} />
            </Field>
            <Field label={t("admin.fMileage")}>
              <input data-testid="part-form-km" type="number" min="0" value={form.km} onChange={set("km")} className={inputCls} />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("admin.fFuelEn")}>
                <input data-testid="part-form-fuel-en" value={form.fuel_en} onChange={set("fuel_en")} className={inputCls} />
              </Field>
              <Field label={t("admin.fFuelRo")}>
                <input data-testid="part-form-fuel-ro" value={form.fuel_ro} onChange={set("fuel_ro")} className={inputCls} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label={t("admin.fDescEn")}>
                <textarea data-testid="part-form-desc-en" rows={3} value={form.desc_en} onChange={set("desc_en")} className={`${inputCls} resize-none`} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label={t("admin.fDescRo")}>
                <textarea data-testid="part-form-desc-ro" rows={3} value={form.desc_ro} onChange={set("desc_ro")} className={`${inputCls} resize-none`} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <PhotoManager
                images={form.images}
                onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))}
                onUploadingChange={setUploading}
              />
            </div>
            <div className="sm:col-span-2 flex gap-3 pt-2">
              <button
                data-testid="part-form-save-btn"
                type="submit"
                disabled={saving || uploading || !form.images.length}
                className="bg-[#4499F7] text-[#0A0B10] font-semibold text-sm rounded-full px-8 py-3 hover:bg-[#6FB1FA] disabled:opacity-60 transition-colors"
              >
                {saving ? t("admin.saving") : t("admin.save")}
              </button>
              <button
                data-testid="part-form-cancel-btn"
                type="button"
                onClick={() => setEditing(null)}
                className="border border-white/15 text-slate-300 text-sm rounded-full px-8 py-3 hover:border-[rgba(68,153,247,0.5)] hover:text-[#4499F7] transition-colors"
              >
                {t("admin.cancel")}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
